// src/features/auth/schemas.ts
import { z } from "zod";
var passwordPolicySchema = z.string().min(10, "Password must be at least 10 characters long").regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/, "Password must include upper, lower, and numeric characters");
var registerSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: passwordPolicySchema,
  firstName: z.string().min(1, "First name is required").max(64, "First name is too long"),
  lastName: z.string().min(1, "Last name is required").max(64, "Last name is too long"),
  inviteCode: z.string().min(1, "Invite code is required").optional(),
  skipOnboarding: z.boolean().optional()
});
var loginSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required")
});
var authUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true })
});
var authOnboardingSchema = z.object({
  projectId: z.string(),
  formId: z.string(),
  draftVersion: z.number().int().min(1)
});
var authSessionSchema = z.object({
  user: authUserSchema,
  accessToken: z.string(),
  onboarding: authOnboardingSchema.optional()
});
var authSuccessSchema = z.object({
  success: z.literal(true),
  data: authSessionSchema,
  meta: z.record(z.unknown()).optional()
});
var apiErrorSchema = z.object({
  success: z.literal(false),
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
    details: z.unknown().optional()
  })
});
var forgotPasswordSchema = z.object({
  email: z.string().email("Enter a valid email address")
});
var resetPasswordSchema = z.object({
  token: z.string().min(1, "Reset token is required"),
  password: passwordPolicySchema
});

// src/features/forms/constants.ts
var TONE_PRESETS = [
  "neutral",
  "friendly",
  "professional",
  "playful",
  "supportive"
];
var FORM_ELEMENT_TYPES = [
  "short_text",
  "long_text",
  "rich_text",
  "image",
  "divider",
  "callout",
  "read_only_text",
  "link",
  "multiple_choice_single",
  "multiple_choice_multi",
  "file_upload",
  "signature",
  "table",
  "email",
  "phone_number",
  "date",
  "time"
];
var TABLE_COLUMN_TYPES = ["text", "signature"];
var CONTENT_ELEMENT_TYPES = [
  "rich_text",
  "image",
  "divider",
  "callout"
];
var LEGACY_DISPLAY_ELEMENT_TYPES = [
  "read_only_text",
  "link"
];
var NON_ANSWERABLE_ELEMENT_TYPES = [
  ...CONTENT_ELEMENT_TYPES,
  ...LEGACY_DISPLAY_ELEMENT_TYPES
];
var DEFAULT_ELEMENT_TYPES = [
  "short_text"
];
var JUMP_CONDITION_OPERATORS = [
  "always",
  "equals",
  "not_equals",
  "includes",
  "not_includes",
  "gte",
  "lte",
  "is_empty",
  "is_not_empty",
  "between"
];
var JUMP_TARGET_END = "__end__";
var JUMP_CONDITION_VALUE_OTHER = "__other__";
var FORM_VERSION_STATUSES = ["draft", "published"];
var FORM_RESPONSE_MODES = ["collect", "shared"];
var PROJECT_MEMBER_ROLES = [
  "owner",
  "admin",
  "editor",
  "viewer"
];
var ASSISTANT_COMPONENT_TYPES = [
  "text",
  "select",
  "upload",
  "signature_pad",
  "custom"
];
var ASSISTANT_PREFILL_STRATEGIES = [
  "none",
  "suggestion",
  "reference"
];
var ASSISTANT_FOLLOW_UP_KINDS = [
  "discovery",
  "validation",
  "suggestion"
];

// src/features/forms/dto.ts
import { z as z3 } from "zod";

// src/features/forms/schemas.ts
import { z as z2 } from "zod";

// src/features/forms/utils/link-url.ts
var ALLOWED_LINK_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];
var ALLOWED_LINK_PROTOCOL_SET = new Set(ALLOWED_LINK_PROTOCOLS);
var parseLinkUrl = (value) => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  try {
    const parsed = new URL(trimmed);
    if (!ALLOWED_LINK_PROTOCOL_SET.has(parsed.protocol)) {
      return null;
    }
    if ((parsed.protocol === "mailto:" || parsed.protocol === "tel:") && parsed.pathname.trim().length === 0) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};
var isAllowedLinkUrl = (value) => parseLinkUrl(value) !== null;
var getSafeLinkHref = (value) => {
  if (typeof value !== "string") {
    return null;
  }
  const parsed = parseLinkUrl(value);
  return parsed ? parsed.toString() : null;
};

// src/features/forms/schemas.ts
var objectIdSchema = z2.string().min(1);
var isoDateTimeSchema = z2.string().datetime({ offset: true });
var tonePresetSchema = z2.enum(TONE_PRESETS);
var formResponseModeSchema = z2.enum(FORM_RESPONSE_MODES);
var formElementTypeSchema = z2.enum(FORM_ELEMENT_TYPES);
var projectMemberRoleSchema = z2.enum(PROJECT_MEMBER_ROLES);
var projectMemberSchema = z2.object({
  userId: objectIdSchema,
  role: projectMemberRoleSchema,
  joinedAt: isoDateTimeSchema
});
var projectSchema = z2.object({
  id: objectIdSchema,
  name: z2.string().min(1),
  slug: z2.string().min(1).regex(
    /^[a-z0-9-]+$/,
    "Slug must contain lowercase letters, numbers, or dashes only"
  ),
  description: z2.string().max(512).optional(),
  createdBy: objectIdSchema,
  members: z2.array(projectMemberSchema),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var questionValidationRuleSchema = z2.discriminatedUnion("kind", [
  z2.object({
    kind: z2.literal("length"),
    min: z2.number().int().min(0).optional(),
    max: z2.number().int().min(1).optional(),
    message: z2.string().optional()
  }),
  z2.object({
    kind: z2.literal("pattern"),
    pattern: z2.string().min(1),
    flags: z2.string().regex(/^[gimsuy]*$/).optional(),
    message: z2.string().optional()
  }),
  z2.object({
    kind: z2.literal("numeric"),
    min: z2.number().optional(),
    max: z2.number().optional(),
    message: z2.string().optional()
  }),
  z2.object({
    kind: z2.literal("file"),
    maxSizeMb: z2.number().positive().optional(),
    accept: z2.array(z2.string().min(1)).optional(),
    message: z2.string().optional()
  })
]);
var questionValidationSchema = z2.object({
  required: z2.boolean().optional(),
  rules: z2.array(questionValidationRuleSchema).default([])
});
var jumpConditionOperatorSchema = z2.enum(JUMP_CONDITION_OPERATORS);
var jumpConditionBetweenValueSchema = z2.object({
  min: z2.number(),
  max: z2.number()
});
var jumpConditionSchema = z2.object({
  questionRef: z2.string().min(1).optional(),
  operator: jumpConditionOperatorSchema,
  value: z2.union([
    z2.string(),
    z2.number(),
    z2.array(z2.string()),
    jumpConditionBetweenValueSchema,
    z2.null()
  ]).optional()
});
var jumpRuleSchema = z2.object({
  id: z2.string().min(1),
  target: z2.string().min(1),
  priority: z2.number().int().min(0).default(0),
  conditions: z2.array(jumpConditionSchema).min(1),
  match: z2.enum(["all", "any"]).default("all")
});
var visibilityConditionSchema = z2.object({
  questionRef: z2.string().min(1),
  // The question whose answer determines visibility
  conditions: z2.array(jumpConditionSchema).min(1),
  match: z2.enum(["all", "any"]).default("all")
});
var conversationMetadataSchema = z2.object({
  acknowledgement: z2.string().optional(),
  reprompt: z2.string().optional(),
  positiveFeedback: z2.string().optional(),
  negativeFeedback: z2.string().optional(),
  typingIndicatorMs: z2.number().int().min(0).max(15e3).optional()
});
var formPageSchema = z2.object({
  id: z2.string().min(1),
  ref: z2.string().min(1),
  title: z2.string().max(200).optional(),
  description: z2.string().max(1024).optional(),
  order: z2.number().int().min(0),
  jumpLogic: z2.array(jumpRuleSchema).default([])
});
var questionBaseSchema = z2.object({
  id: z2.string().min(1),
  ref: z2.string().min(1),
  title: z2.string().min(1),
  description: z2.string().max(1024).optional(),
  pageId: z2.string().min(1).optional(),
  required: z2.boolean().default(false),
  validation: questionValidationSchema.optional(),
  conversation: conversationMetadataSchema.optional(),
  visibleWhen: z2.preprocess(
    (value) => value === null ? void 0 : value,
    visibilityConditionSchema.optional()
  )
});
var contentQuestionBaseSchema = questionBaseSchema.extend({
  title: z2.string().max(200).default(""),
  required: z2.literal(false).default(false)
});
var voiceInputSchema = z2.object({
  enabled: z2.boolean().default(true)
});
var multipleChoiceOptionSchema = z2.object({
  id: z2.string().min(1),
  label: z2.string().min(1),
  value: z2.string().min(1),
  description: z2.string().optional()
});
var contentAlignmentSchema = z2.enum(["left", "center", "right"]);
var imageAssetSchema = z2.object({
  fileKey: z2.string().min(1),
  fileName: z2.string().min(1),
  fileSize: z2.number().int().min(0),
  mimeType: z2.string().min(1),
  uploadedAt: isoDateTimeSchema.optional()
});
var shortTextQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("short_text"),
  placeholder: z2.string().optional(),
  multiline: z2.literal(false).optional(),
  voiceInput: voiceInputSchema.optional()
});
var longTextQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("long_text"),
  placeholder: z2.string().optional(),
  multiline: z2.literal(true).default(true),
  voiceInput: voiceInputSchema.optional()
});
var richTextQuestionSchema = contentQuestionBaseSchema.extend({
  type: z2.literal("rich_text"),
  markdown: z2.string().min(1),
  styleVariant: z2.enum(["body", "title", "subtitle"]).optional()
});
var imageQuestionSchema = contentQuestionBaseSchema.extend({
  type: z2.literal("image"),
  url: z2.string().url().max(2048),
  alt: z2.string().min(1).max(300),
  caption: z2.string().max(1024).optional(),
  source: z2.enum(["upload", "url"]).optional(),
  asset: imageAssetSchema.optional(),
  width: z2.enum(["auto", "full", "wide", "narrow"]).optional(),
  alignment: contentAlignmentSchema.optional()
});
var dividerQuestionSchema = contentQuestionBaseSchema.extend({
  type: z2.literal("divider"),
  style: z2.enum(["line", "spacer"]).optional(),
  spacing: z2.enum(["sm", "md", "lg"]).optional()
});
var calloutQuestionSchema = contentQuestionBaseSchema.extend({
  type: z2.literal("callout"),
  markdown: z2.string().min(1),
  tone: z2.enum(["info", "success", "warning", "error"]).optional(),
  icon: z2.string().max(32).optional()
});
var readOnlyTextQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("read_only_text"),
  required: z2.literal(false).default(false)
});
var linkQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("link"),
  url: z2.string().min(1).refine(isAllowedLinkUrl, {
    message: "Link URL must use one of: http, https, mailto, tel"
  }),
  linkText: z2.string().min(1).max(300).optional(),
  openInNewTab: z2.boolean().default(true),
  required: z2.literal(false).default(false)
});
var singleChoiceQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("multiple_choice_single"),
  options: z2.array(multipleChoiceOptionSchema).min(1),
  allowOther: z2.boolean().default(false),
  shuffle: z2.boolean().default(false)
});
var multiChoiceQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("multiple_choice_multi"),
  options: z2.array(multipleChoiceOptionSchema).min(1),
  minSelections: z2.number().int().min(1).optional(),
  maxSelections: z2.number().int().min(1).optional(),
  allowOther: z2.boolean().default(false),
  shuffle: z2.boolean().default(false)
});
var fileUploadQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("file_upload"),
  maxFiles: z2.number().int().min(1).max(10).default(1),
  maxSizeMb: z2.number().positive().default(10),
  accept: z2.array(z2.string().min(1)).default([]),
  storageProvider: z2.enum(["s3", "gcs", "azure", "custom"]).default("s3"),
  // LLM-generated payloads sometimes send `storageConfig: null`; treat it like undefined
  storageConfig: z2.record(z2.unknown()).optional().nullable()
});
var signatureQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("signature"),
  padTitle: z2.string().optional(),
  padSubtitle: z2.string().optional(),
  requireTypedName: z2.boolean().default(false)
});
var tableColumnTypeSchema = z2.enum(TABLE_COLUMN_TYPES);
var tableColumnBaseSchema = z2.object({
  id: z2.string().min(1).regex(/^[A-Za-z0-9_-]+$/),
  label: z2.string().min(1),
  required: z2.boolean().default(false)
});
var tableColumnSchema = z2.discriminatedUnion("type", [
  tableColumnBaseSchema.extend({
    type: z2.literal("text"),
    placeholder: z2.string().optional()
  }),
  tableColumnBaseSchema.extend({
    type: z2.literal("signature"),
    requireTypedName: z2.boolean().default(false)
  })
]);
var tableRowSchema = z2.object({
  id: z2.string().min(1).regex(/^[A-Za-z0-9_-]+$/),
  label: z2.string().min(1)
});
var tableQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("table"),
  columns: z2.array(tableColumnSchema).min(1),
  rows: z2.array(tableRowSchema).min(1)
});
var emailQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("email"),
  placeholder: z2.string().optional()
  // Placeholder is hint text, not an email address
});
var phoneQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("phone_number"),
  defaultCountry: z2.string().length(2).optional(),
  allowInternational: z2.boolean().default(true)
});
var dateQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("date"),
  placeholder: z2.string().optional(),
  minDate: z2.string().optional(),
  // ISO date string
  maxDate: z2.string().optional(),
  // ISO date string
  dateFormat: z2.enum(["MM/dd/yyyy", "dd/MM/yyyy", "yyyy-MM-dd"]).default("MM/dd/yyyy")
});
var timeQuestionSchema = questionBaseSchema.extend({
  type: z2.literal("time"),
  placeholder: z2.string().optional(),
  minTime: z2.string().optional(),
  // HH:mm
  maxTime: z2.string().optional()
  // HH:mm
});
var formQuestionSchema = z2.discriminatedUnion("type", [
  shortTextQuestionSchema,
  longTextQuestionSchema,
  richTextQuestionSchema,
  imageQuestionSchema,
  dividerQuestionSchema,
  calloutQuestionSchema,
  readOnlyTextQuestionSchema,
  linkQuestionSchema,
  singleChoiceQuestionSchema,
  multiChoiceQuestionSchema,
  fileUploadQuestionSchema,
  signatureQuestionSchema,
  tableQuestionSchema,
  emailQuestionSchema,
  phoneQuestionSchema,
  dateQuestionSchema,
  timeQuestionSchema
]);
var formElementPaletteSchema = z2.array(formElementTypeSchema);
var hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
var formThemeSchema = z2.object({
  primaryColor: z2.string().regex(hexColorRegex, "Must be a valid hex color (e.g., #2563eb)").optional(),
  secondaryColor: z2.string().regex(hexColorRegex, "Must be a valid hex color").optional(),
  accentColor: z2.string().regex(hexColorRegex, "Must be a valid hex color").optional(),
  backgroundColor: z2.string().regex(hexColorRegex, "Must be a valid hex color").optional(),
  fontFamily: z2.string().max(100).optional(),
  headingFontFamily: z2.string().max(100).optional(),
  logoUrl: z2.string().url().max(500).optional(),
  coverStyle: z2.enum(["none", "solid", "gradient", "image"]).optional(),
  coverColor: z2.string().regex(hexColorRegex, "Must be a valid hex color").optional(),
  coverGradientFrom: z2.string().regex(hexColorRegex, "Must be a valid hex color").optional(),
  coverGradientTo: z2.string().regex(hexColorRegex, "Must be a valid hex color").optional(),
  coverImageUrl: z2.string().url().max(500).optional(),
  icon: z2.string().max(12).optional()
});
var formLayoutSchema = z2.object({
  variant: z2.enum(["standard", "compact", "conversational"]).default("conversational"),
  showProgress: z2.boolean().default(true),
  progressDisplay: z2.enum(["pages", "questions", "percentage"]).default("questions"),
  alignment: z2.enum(["left", "center", "right"]).default("center")
});
var formCopySchema = z2.object({
  introduction: z2.string().optional(),
  thankYou: z2.string().optional(),
  completionCta: z2.string().optional(),
  emptyState: z2.string().optional(),
  estimatedMinutes: z2.number().int().min(0).max(60).optional()
});
var emailNotificationSettingsSchema = z2.object({
  enabled: z2.boolean().default(false)
});
var webhookSettingsSchema = z2.object({
  enabled: z2.boolean().default(false),
  url: z2.string().url().optional()
});
var postSubmissionActionsSchema = z2.object({
  emailNotification: emailNotificationSettingsSchema.optional(),
  webhook: webhookSettingsSchema.optional()
});
var headlessRateLimitsSchema = z2.object({
  perIpPerMinute: z2.number().int().min(1).max(1e4).default(30),
  perIpPerHour: z2.number().int().min(1).max(1e5).default(200),
  perIpPerDay: z2.number().int().min(1).max(1e6).default(2e3),
  perFormPerDay: z2.number().int().min(1).max(1e7).default(5e4)
});
var headlessCaptchaSettingsSchema = z2.object({
  provider: z2.enum(["turnstile", "hcaptcha", "none"]).default("none"),
  siteKey: z2.string().optional(),
  secretKey: z2.string().optional()
});
var formHeadlessSettingsSchema = z2.object({
  enabled: z2.boolean().default(false),
  allowedOrigins: z2.array(z2.string().min(1)).default([]),
  limits: headlessRateLimitsSchema.default({}),
  captcha: headlessCaptchaSettingsSchema.default({ provider: "none" })
});
var formUiMetadataSchema = z2.object({
  copy: formCopySchema.default({}),
  layout: formLayoutSchema.default({}),
  theme: formThemeSchema.optional(),
  customCss: z2.string().optional(),
  postSubmissionActions: postSubmissionActionsSchema.optional()
});
var formConversationRoleSchema = z2.enum([
  "system",
  "user",
  "assistant"
]);
var assistantComponentSchema = z2.enum(ASSISTANT_COMPONENT_TYPES);
var assistantPrefillSchema = z2.object({
  strategy: z2.enum(ASSISTANT_PREFILL_STRATEGIES).default("none"),
  value: z2.string().optional()
}).passthrough();
var assistantOptionJumpSchema = z2.object({
  targetRef: z2.string().min(1),
  condition: jumpConditionSchema.optional()
}).passthrough();
var assistantSuggestedQuestionOptionSchema = z2.object({
  id: z2.string().min(1).optional(),
  label: z2.string().min(1),
  value: z2.union([z2.string().min(1), z2.number().min(1)]),
  description: z2.string().optional(),
  jump: assistantOptionJumpSchema.optional()
}).passthrough();
var assistantJumpRuleSchema = z2.object({
  id: z2.string().min(1).optional(),
  targetRef: z2.string().min(1),
  priority: z2.number().int().min(0).default(0),
  conditions: z2.array(jumpConditionSchema).default([]),
  match: z2.enum(["all", "any"]).default("all")
}).passthrough();
var assistantAnalyticsSchema = z2.object({
  category: z2.string().min(1).optional(),
  priority: z2.number().int().min(0).optional(),
  dependencies: z2.array(z2.string().min(1)).default([])
}).catchall(z2.unknown());
var assistantSuggestedQuestionSchema = z2.object({
  id: z2.string().min(1).optional(),
  ref: z2.string().min(1).optional(),
  title: z2.string().min(1),
  type: formElementTypeSchema,
  description: z2.string().max(1024).optional(),
  component: assistantComponentSchema.default("text"),
  required: z2.boolean().optional(),
  placeholder: z2.string().optional(),
  markdown: z2.string().optional(),
  styleVariant: z2.string().optional(),
  url: z2.string().optional(),
  alt: z2.string().optional(),
  caption: z2.string().optional(),
  source: z2.enum(["upload", "url"]).optional(),
  asset: imageAssetSchema.optional(),
  width: z2.enum(["auto", "full", "wide", "narrow"]).optional(),
  alignment: contentAlignmentSchema.optional(),
  linkText: z2.string().optional(),
  openInNewTab: z2.boolean().optional(),
  tone: z2.enum(["info", "success", "warning", "error"]).optional(),
  icon: z2.string().optional(),
  voiceInput: voiceInputSchema.optional(),
  validation: questionValidationSchema.optional(),
  options: z2.array(assistantSuggestedQuestionOptionSchema).default([]),
  jumpLogic: z2.array(assistantJumpRuleSchema).default([]),
  visibleWhen: z2.preprocess(
    (value) => value === null ? void 0 : value,
    visibilityConditionSchema.optional()
  )
}).passthrough();
var uiDraftsSchema = z2.object({
  introduction: z2.string().optional(),
  thankYou: z2.string().optional(),
  estimatedMinutes: z2.number().int().min(1).max(60).optional()
});
var assistantConfigurationLayoutSchema = z2.object({
  variant: z2.enum(["standard", "compact", "conversational"]).optional(),
  showProgress: z2.boolean().optional(),
  progressDisplay: z2.enum(["pages", "questions", "percentage"]).optional(),
  alignment: z2.enum(["left", "center", "right"]).optional()
});
var assistantConfigurationCopySchema = z2.object({
  estimatedMinutes: z2.number().int().min(0).max(60).optional(),
  completionCta: z2.string().min(1).max(200).optional()
});
var assistantConfigurationPageSchema = formPageSchema.extend({
  jumpLogic: z2.array(jumpRuleSchema).optional()
});
var assistantConfigurationPagesSchema = z2.array(
  assistantConfigurationPageSchema
);
var assistantConfigurationQuestionPageIdsSchema = z2.record(
  z2.string().min(1),
  z2.union([z2.string().min(1), z2.null()])
);
var logicPatchesJumpRuleSchema = z2.preprocess((value) => {
  if (!value || typeof value !== "object") {
    return value;
  }
  const record = value;
  if (typeof record.target !== "string" && typeof record.targetRef === "string") {
    return { ...record, target: record.targetRef };
  }
  return value;
}, jumpRuleSchema);
var formConversationLogicPatchSchema = z2.object({
  jumpLogic: z2.array(logicPatchesJumpRuleSchema).optional(),
  visibleWhen: visibilityConditionSchema.nullable().optional()
});
var formConversationLogicPatchesSchema = z2.record(
  z2.string().min(1),
  formConversationLogicPatchSchema
);
var formConversationMessageMetadataSchema = z2.object({
  summary: z2.string().optional(),
  routing: z2.object({
    intent: z2.string().min(1),
    variant: z2.string().min(1).optional()
  }).optional(),
  discovery: z2.object({
    phase: z2.literal("discovery").default("discovery"),
    understanding: z2.object({
      purpose: z2.string().nullable(),
      audience: z2.string().nullable(),
      data_needs: z2.array(z2.string().min(1)).nullable(),
      compliance_relevant: z2.boolean(),
      complexity: z2.enum(["simple", "medium", "complex"])
    }),
    hypothesis_presented: z2.boolean().optional(),
    next_focus: z2.string().min(1),
    // New hypothesis-first flow: hypothesizing → confirming → ready
    // Legacy values kept for backward compatibility: exploring, gathering, validating
    status: z2.enum([
      "hypothesizing",
      "confirming",
      "ready",
      "exploring",
      "gathering",
      "validating"
    ]),
    open_questions: z2.array(z2.string().min(1)).default([]),
    // DEPRECATED: UI drafts - kept for backwards compatibility
    // New location: root level ui_drafts field
    ui_drafts: uiDraftsSchema.optional()
  }).optional(),
  suggestedQuestions: z2.array(assistantSuggestedQuestionSchema).default([]),
  // UI drafts - canonical location (also accepted at discovery.ui_drafts for backwards compat)
  ui_drafts: uiDraftsSchema.optional(),
  // Configuration intent payloads (applied by backend metadata handlers)
  layout: assistantConfigurationLayoutSchema.optional(),
  theme: formThemeSchema.optional(),
  pages: assistantConfigurationPagesSchema.optional(),
  questionPageIds: assistantConfigurationQuestionPageIdsSchema.optional(),
  copy: assistantConfigurationCopySchema.optional(),
  logicPatches: formConversationLogicPatchesSchema.optional()
}).passthrough();
var formConversationMessageSchema = z2.object({
  id: z2.string().min(1).optional(),
  role: formConversationRoleSchema,
  content: z2.string().min(0),
  createdAt: isoDateTimeSchema,
  metadata: formConversationMessageMetadataSchema.optional()
});
var respondentConversationMetadataSchema = z2.object({
  action: z2.enum([
    "ask_question",
    "validate_answer",
    "record_answer",
    "complete_form"
  ]),
  currentQuestionRef: z2.string(),
  componentType: z2.enum(["select", "upload", "signature_pad"]).nullable(),
  validationResult: z2.object({
    valid: z2.boolean(),
    errors: z2.array(z2.string())
  }).optional(),
  answer: z2.unknown().optional(),
  nextQuestionRef: z2.string().nullable().optional(),
  progressPercentage: z2.number().min(0).max(100),
  shouldRenderComponent: z2.boolean()
});
var orchestratorPhaseSchema = z2.enum([
  "presenting",
  "awaiting_answer",
  "validating",
  "transitioning",
  "completed"
]);
var orchestratorMetadataSchema = z2.object({
  phase: orchestratorPhaseSchema,
  currentQuestionRef: z2.string().nullable(),
  componentType: z2.enum(["select", "upload", "signature_pad"]).nullable(),
  progressPercentage: z2.number().min(0).max(100),
  shouldRenderComponent: z2.boolean(),
  isComplete: z2.boolean(),
  /** Collected answers - only included when isComplete is true */
  answers: z2.record(z2.unknown()).optional()
});
var orchestratorConversationMetadataSchema = orchestratorMetadataSchema;
var conversationSettingsSchema = z2.object({
  tonePreset: tonePresetSchema,
  elementPalette: formElementPaletteSchema.default([]),
  typingSpeedWpm: z2.number().int().min(30).max(200).default(120),
  idleTimeoutMs: z2.number().int().min(0).max(12e4).default(45e3),
  handoffMessage: z2.string().optional(),
  transcript: z2.array(formConversationMessageSchema).default([])
});
var jsonSchema202012Schema = z2.object({
  $schema: z2.string().url().refine(
    (value) => value.includes("2020-12"),
    "JSON schema must declare 2020-12 draft"
  )
}).catchall(z2.unknown());
var formVersionStatusSchema = z2.enum(FORM_VERSION_STATUSES);
var formVersionSchema = z2.object({
  id: objectIdSchema,
  formId: objectIdSchema,
  projectId: objectIdSchema,
  version: z2.number().int().min(1),
  status: formVersionStatusSchema,
  label: z2.string().optional(),
  questions: z2.array(formQuestionSchema).default([]),
  pages: z2.array(formPageSchema).default([]),
  jsonSchema: jsonSchema202012Schema,
  conversation: conversationSettingsSchema,
  ui: formUiMetadataSchema,
  discovery: formConversationMessageMetadataSchema.shape.discovery.optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  publishedAt: isoDateTimeSchema.optional(),
  schemaChecksum: z2.string().optional(),
  releaseNotes: z2.string().optional(),
  shareUrl: z2.string().url().optional(),
  embedCode: z2.string().optional()
});
var slugSchema = z2.string().min(1).regex(
  /^[a-z0-9-]+$/,
  "Slug must contain lowercase letters, numbers, or dashes only"
);
var publicFormVersionSchema = z2.object({
  id: objectIdSchema,
  formId: objectIdSchema,
  projectId: objectIdSchema,
  projectSlug: slugSchema,
  formSlug: slugSchema,
  responseMode: formResponseModeSchema.default("collect"),
  version: z2.number().int().min(1),
  questions: z2.array(formQuestionSchema),
  pages: z2.array(formPageSchema),
  ui: formUiMetadataSchema.pick({ copy: true, layout: true, theme: true }),
  conversation: conversationSettingsSchema.pick({ tonePreset: true }),
  jsonSchema: jsonSchema202012Schema,
  publishedAt: isoDateTimeSchema.optional()
});
var formSchema = z2.object({
  id: objectIdSchema,
  projectId: objectIdSchema,
  name: z2.string().min(1),
  slug: z2.string().min(1).regex(
    /^[a-z0-9-]+$/,
    "Slug must contain lowercase letters, numbers, or dashes only"
  ),
  description: z2.string().optional(),
  status: z2.enum(["draft", "published", "archived"]).default("draft"),
  responseMode: formResponseModeSchema.default("collect"),
  supportedElements: formElementPaletteSchema,
  activeVersionId: objectIdSchema.optional(),
  draftVersionId: objectIdSchema.optional(),
  createdBy: objectIdSchema,
  archivedBy: objectIdSchema.optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  archivedAt: isoDateTimeSchema.optional(),
  responseCount: z2.number().int().min(0).optional(),
  headless: formHeadlessSettingsSchema.default({}),
  lastSubmissionAt: isoDateTimeSchema.optional(),
  lastActivityAt: isoDateTimeSchema.optional(),
  draftVersionLabel: z2.string().optional(),
  publishedVersionLabel: z2.string().optional(),
  totalViews: z2.number().int().min(0).optional(),
  starts: z2.number().int().min(0).optional(),
  completionRate: z2.number().min(0).max(1).optional(),
  averageCompletionTimeMs: z2.number().int().min(0).optional(),
  dropOffRate: z2.number().min(0).max(1).optional()
});
var projectWithFormsSchema = projectSchema.extend({
  forms: z2.array(formSchema)
});
var formStatsSchema = z2.object({
  totalResponses: z2.number().int().min(0).default(0),
  completionRate: z2.number().min(0).max(1).optional(),
  averageCompletionTimeMs: z2.number().int().min(0).optional(),
  lastSubmissionAt: isoDateTimeSchema.optional(),
  totalViews: z2.number().int().min(0).optional(),
  starts: z2.number().int().min(0).optional(),
  dropOffRate: z2.number().min(0).max(1).optional()
});
var submissionMetadataSchema = z2.object({
  userAgent: z2.string().optional(),
  ipAddress: z2.string().optional(),
  referrer: z2.string().optional()
});
var submissionStatusSchema = z2.enum([
  "in_progress",
  "completed",
  "abandoned"
]);
var responseCaptureModeSchema = z2.enum([
  "standard",
  "conversational",
  "shared"
]);
var submissionSummarySchema = z2.object({
  id: objectIdSchema,
  projectId: objectIdSchema,
  formId: objectIdSchema,
  versionNumber: z2.number().int().min(1),
  status: submissionStatusSchema,
  captureMode: responseCaptureModeSchema.optional(),
  progressPercentage: z2.number().min(0).max(100).optional(),
  lastQuestionRef: z2.string().optional(),
  lastPageId: z2.string().optional(),
  lastInteractionAt: isoDateTimeSchema.optional(),
  abandonCandidateAt: isoDateTimeSchema.optional(),
  abandonedAt: isoDateTimeSchema.optional(),
  startedAt: isoDateTimeSchema,
  submittedAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.optional(),
  durationMs: z2.number().int().min(0).optional(),
  metadata: submissionMetadataSchema.optional()
});
var responseConversationStateSchema = z2.object({
  currentQuestionRef: z2.string().optional(),
  completedQuestionRefs: z2.array(z2.string()).default([]),
  sessionStartedAt: isoDateTimeSchema.optional(),
  lastActivityAt: isoDateTimeSchema.optional(),
  attemptCount: z2.record(z2.number()).default({}),
  progressPercentage: z2.number().min(0).max(100).optional()
});
var responseSchema = z2.object({
  id: objectIdSchema,
  projectId: objectIdSchema,
  formId: objectIdSchema,
  versionNumber: z2.number().int().min(1),
  status: submissionStatusSchema,
  captureMode: responseCaptureModeSchema.optional(),
  progressPercentage: z2.number().min(0).max(100).optional(),
  lastQuestionRef: z2.string().optional(),
  lastPageId: z2.string().optional(),
  lastInteractionAt: isoDateTimeSchema.optional(),
  abandonCandidateAt: isoDateTimeSchema.optional(),
  abandonedAt: isoDateTimeSchema.optional(),
  answers: z2.record(z2.unknown()),
  skippedQuestionRefs: z2.array(z2.string()).default([]),
  questionPath: z2.array(z2.string()).default([]),
  questionMetadata: z2.record(z2.unknown()).default({}),
  startedAt: isoDateTimeSchema,
  submittedAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.optional(),
  sessionId: z2.string().optional(),
  conversationalMode: z2.boolean().optional(),
  conversationTranscript: z2.array(formConversationMessageSchema).optional(),
  conversationState: responseConversationStateSchema.optional(),
  metadata: submissionMetadataSchema.optional(),
  fileUploads: z2.array(
    z2.object({
      questionRef: z2.string(),
      fileKey: z2.string(),
      fileName: z2.string(),
      fileSize: z2.number().int().min(0),
      mimeType: z2.string()
    })
  ).optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var analyticsTrendDateSchema = z2.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var formAnalyticsFunnelNodeSchema = z2.object({
  questionRef: z2.string().min(1),
  questionTitle: z2.string().optional(),
  reached: z2.number().int().min(0),
  abandoned: z2.number().int().min(0),
  dropOffRate: z2.number().min(0).optional()
});
var formAnalyticsLandingSchema = z2.object({
  questionRef: z2.string().min(1),
  questionTitle: z2.string().optional(),
  count: z2.number().int().min(0),
  averageProgressPercentage: z2.number().min(0).max(100).optional()
});
var formAnalyticsTrendPointSchema = z2.object({
  date: analyticsTrendDateSchema,
  starts: z2.number().int().min(0),
  completed: z2.number().int().min(0),
  abandoned: z2.number().int().min(0)
});
var formAnalyticsSummarySchema = z2.object({
  starts: z2.number().int().min(0),
  completed: z2.number().int().min(0),
  inProgress: z2.number().int().min(0),
  abandoned: z2.number().int().min(0),
  completionRate: z2.number().min(0).max(1).optional(),
  churnRate: z2.number().min(0).max(1).optional(),
  averageCompletionTimeMs: z2.number().int().min(0).optional(),
  averageProgressAtChurn: z2.number().min(0).max(100).optional(),
  medianProgressAtChurn: z2.number().min(0).max(100).optional()
});
var formAnalyticsOverviewSchema = z2.object({
  window: z2.object({
    dateFrom: isoDateTimeSchema,
    dateTo: isoDateTimeSchema
  }),
  summary: formAnalyticsSummarySchema,
  funnel: z2.array(formAnalyticsFunnelNodeSchema),
  topDropOffPoints: z2.array(formAnalyticsFunnelNodeSchema),
  topDropOff: formAnalyticsFunnelNodeSchema.optional(),
  churnByLanding: z2.array(formAnalyticsLandingSchema),
  trend: z2.array(formAnalyticsTrendPointSchema),
  partialSessions: z2.array(submissionSummarySchema)
});
var formAnalyticsEventSchema = z2.object({
  event: z2.enum(["form_viewed", "form_started", "form_completed"]),
  durationMs: z2.number().int().min(0).optional()
});
var respondentStreamEventSchema = z2.discriminatedUnion("type", [
  z2.object({ type: z2.literal("token"), text: z2.string() }),
  z2.object({ type: z2.literal("metadata"), data: orchestratorMetadataSchema }),
  z2.object({
    type: z2.literal("complete"),
    responseId: z2.string(),
    assistantMessage: formConversationMessageSchema,
    sessionToken: z2.string().optional()
  })
]);
var conversationSessionStateSchema = z2.object({
  responseId: z2.string(),
  transcript: z2.array(formConversationMessageSchema),
  metadata: orchestratorMetadataSchema
});

// src/features/forms/dto.ts
var createProjectRequestSchema = projectSchema.pick({ name: true, description: true }).extend({
  slug: projectSchema.shape.slug.optional(),
  description: projectSchema.shape.description.optional()
});
var updateProjectRequestSchema = z3.object({
  name: z3.string().min(1).max(120).trim().optional(),
  description: z3.string().max(512).optional().nullable()
}).refine((data) => Object.values(data).some((value) => value !== void 0), {
  message: "At least one field must be provided"
});
var createFormRequestSchema = z3.object({
  name: z3.string().min(1),
  slug: z3.string().min(1).regex(/^[a-z0-9-]+$/, "Slug must contain lowercase letters, numbers, or dashes only"),
  description: z3.string().max(512).optional(),
  supportedElements: formElementPaletteSchema.default([...FORM_ELEMENT_TYPES])
});
var updateFormRequestSchema = z3.object({
  name: z3.string().min(1).max(120).optional(),
  description: z3.string().max(512).optional().nullable(),
  headless: formHeadlessSettingsSchema.optional(),
  responseMode: formSchema.shape.responseMode.optional()
}).refine((data) => Object.values(data).some((value) => value !== void 0), {
  message: "At least one field must be provided"
});
var formVersionContentSchema = z3.object({
  questions: z3.array(formQuestionSchema).min(1),
  pages: z3.array(formPageSchema).default([]),
  jsonSchema: jsonSchema202012Schema,
  conversation: conversationSettingsSchema,
  ui: formUiMetadataSchema,
  // Reuse the discovery schema from the main metadata schema to avoid duplication
  discovery: formConversationMessageMetadataSchema.shape.discovery,
  label: z3.string().min(1).max(120).optional()
});
var createFormVersionRequestSchema = formVersionContentSchema.partial().default({});
var updateFormVersionRequestSchema = formVersionContentSchema.omit({ jsonSchema: true }).extend({
  questions: z3.array(formQuestionSchema),
  // Allow empty during discovery
  jsonSchema: jsonSchema202012Schema.optional()
  // Server regenerates from questions
});
var publishFormVersionRequestSchema = z3.object({
  label: z3.string().min(1).max(120).optional(),
  releaseNotes: z3.string().max(2e3).optional()
}).default({});
var booleanQueryParamSchema = z3.union([z3.boolean(), z3.enum(["true", "false"])]).optional().transform((value) => {
  if (value === void 0) {
    return void 0;
  }
  if (typeof value === "boolean") {
    return value;
  }
  return value === "true";
});
var listFormsQuerySchema = z3.object({
  search: z3.string().trim().min(1).max(120).optional(),
  status: formSchema.shape.status.optional(),
  includeArchived: booleanQueryParamSchema,
  limit: z3.coerce.number().int().min(1).max(100).optional(),
  cursor: z3.string().optional(),
  sortBy: z3.enum(["updated", "created", "responses"]).optional()
});
var duplicateFormRequestSchema = z3.object({
  targetProjectId: objectIdSchema.optional(),
  name: z3.string().min(1).max(120).optional(),
  slug: z3.string().min(1).regex(/^[a-z0-9-]+$/, "Slug must contain lowercase letters, numbers, or dashes only").optional(),
  sourceVersionId: objectIdSchema.optional(),
  includeDraft: z3.boolean().optional()
}).default({});
var archiveFormRequestSchema = z3.object({
  reason: z3.string().max(512).optional()
}).default({});
var listSubmissionsQuerySchema = z3.object({
  status: submissionStatusSchema.optional(),
  versionNumber: z3.coerce.number().int().min(1).optional(),
  dateFrom: z3.string().datetime({ offset: true }).optional(),
  dateTo: z3.string().datetime({ offset: true }).optional(),
  search: z3.string().trim().max(120).optional(),
  sortBy: z3.enum(["submittedAt", "startedAt"]).optional(),
  sortOrder: z3.enum(["asc", "desc"]).optional(),
  limit: z3.coerce.number().int().min(1).max(100).optional(),
  cursor: z3.string().optional()
});
var submissionExportFormatSchema = z3.enum(["csv", "json"]);
var responseExportFormatSchema = z3.enum(["markdown", "pdf"]);
var trackFormEventRequestSchema = formAnalyticsEventSchema;
var createResponseSessionRequestSchema = z3.object({
  projectId: objectIdSchema,
  formId: objectIdSchema,
  versionNumber: z3.coerce.number().int().positive(),
  startedAt: isoDateTimeSchema,
  answers: z3.record(z3.unknown()).default({}),
  skippedQuestionRefs: z3.array(z3.string().min(1)).default([]),
  questionPath: z3.array(z3.string().min(1)).default([]),
  progressPercentage: z3.number().min(0).max(100).optional(),
  lastQuestionRef: z3.string().min(1).optional(),
  lastPageId: z3.string().min(1).optional()
});
var updateResponseSessionProgressRequestSchema = z3.object({
  answers: z3.record(z3.unknown()).default({}),
  skippedQuestionRefs: z3.array(z3.string().min(1)).default([]),
  questionPath: z3.array(z3.string().min(1)).default([]),
  progressPercentage: z3.number().min(0).max(100).optional(),
  lastQuestionRef: z3.string().min(1).optional(),
  lastPageId: z3.string().min(1).optional()
});
var updateSharedFormStateRequestSchema = z3.object({
  answers: z3.record(z3.unknown()).refine(
    (answers) => Object.keys(answers).length > 0,
    "At least one answer must be provided"
  ).refine(
    (answers) => Object.keys(answers).length <= 50,
    "Shared state updates cannot contain more than 50 answer keys"
  )
});
var sharedFormStateResponseSchema = z3.object({
  responseId: objectIdSchema,
  answers: z3.record(z3.unknown()),
  updatedAt: isoDateTimeSchema
});
var markResponseSessionLeaveRequestSchema = z3.object({
  answers: z3.record(z3.unknown()).optional(),
  skippedQuestionRefs: z3.array(z3.string().min(1)).optional(),
  questionPath: z3.array(z3.string().min(1)).optional(),
  progressPercentage: z3.number().min(0).max(100).optional(),
  lastQuestionRef: z3.string().min(1).optional(),
  lastPageId: z3.string().min(1).optional(),
  sessionToken: z3.string().min(1).optional()
});
var completeResponseSessionRequestSchema = z3.object({
  answers: z3.record(z3.unknown()),
  skippedQuestionRefs: z3.array(z3.string().min(1)).default([]),
  questionPath: z3.array(z3.string().min(1)).default([]),
  progressPercentage: z3.number().min(0).max(100).optional(),
  lastQuestionRef: z3.string().min(1).optional(),
  lastPageId: z3.string().min(1).optional()
});
var createResponseSessionResponseSchema = z3.object({
  responseId: objectIdSchema,
  sessionToken: z3.string().min(1),
  startedAt: isoDateTimeSchema
});
var completeResponseSessionResponseSchema = z3.object({
  responseId: objectIdSchema,
  submittedAt: isoDateTimeSchema
});
var getFormAnalyticsOverviewQuerySchema = z3.object({
  dateFrom: isoDateTimeSchema.optional(),
  dateTo: isoDateTimeSchema.optional(),
  versionNumber: z3.coerce.number().int().min(1).optional(),
  partialLimit: z3.coerce.number().int().min(1).max(100).optional()
});
var getFormAnalyticsOverviewResponseSchema = z3.object({
  overview: formAnalyticsOverviewSchema
});
var getResponseSessionResponseSchema = z3.object({
  response: responseSchema
});
var questionDocumentationSchema = z3.object({
  title: z3.string().min(1),
  description: z3.string().optional(),
  type: z3.string().min(1),
  format: z3.string().optional(),
  required: z3.boolean(),
  questionType: z3.enum(FORM_ELEMENT_TYPES),
  options: z3.array(z3.object({ id: z3.string(), label: z3.string() })).optional()
});
var formSchemaMetaSchema = z3.object({
  formId: objectIdSchema,
  formName: z3.string().min(1),
  versionNumber: z3.number().int().min(1),
  versionStatus: formVersionStatusSchema,
  generatedAt: isoDateTimeSchema
});
var formSchemaResultSchema = z3.object({
  schema: jsonSchema202012Schema,
  meta: formSchemaMetaSchema,
  answerDocumentation: z3.record(questionDocumentationSchema)
});

// src/features/forms/table-cells.ts
var isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
var getCells = (answer) => {
  if (!isRecord(answer) || !isRecord(answer.cells)) {
    return {};
  }
  const cells = {};
  for (const [rowId, rowValue] of Object.entries(answer.cells)) {
    if (isRecord(rowValue)) {
      cells[rowId] = rowValue;
    }
  }
  return cells;
};
var hasSignatureArtifact = (value) => {
  if (typeof value === "string") {
    return value.trim().startsWith("http");
  }
  if (!isRecord(value)) {
    return false;
  }
  return typeof value.url === "string" && value.url.trim().length > 0;
};
var isTypedNameMissing = (column, value) => {
  if (!column.requireTypedName || !hasSignatureArtifact(value)) {
    return false;
  }
  if (!isRecord(value)) {
    return true;
  }
  return typeof value.typedName !== "string" || value.typedName.trim() === "";
};
var isTableCellEmpty = (column, value) => {
  if (value === void 0 || value === null) {
    return true;
  }
  switch (column.type) {
    case "text":
      return typeof value !== "string" || value.trim() === "";
    case "signature":
      return !hasSignatureArtifact(value);
    default:
      return true;
  }
};
var validateTableCell = (column, value) => {
  if (isTableCellEmpty(column, value)) {
    return null;
  }
  switch (column.type) {
    case "text":
      return typeof value === "string" ? null : "Text cell must be a string";
    case "signature": {
      if (typeof value === "string") {
        return value.trim().startsWith("http") ? null : "Signature cell must contain a signature image";
      }
      if (!isRecord(value) || !hasSignatureArtifact(value)) {
        return "Signature cell must contain a signature image";
      }
      if (isTypedNameMissing(column, value)) {
        return "Signature cell requires a typed name";
      }
      return null;
    }
    default:
      return "Unsupported table column type";
  }
};
var getRequiredTableColumns = (question) => {
  const explicitlyRequired = question.columns.filter((column) => column.required);
  if (explicitlyRequired.length > 0) {
    return explicitlyRequired;
  }
  return question.required || question.validation?.required ? question.columns : [];
};
var isTableAnswered = (question, answer) => {
  const cells = getCells(answer);
  const requiredColumns = getRequiredTableColumns(question);
  if (requiredColumns.length === 0) {
    return question.rows.some(
      (row) => question.columns.some((column) => !isTableCellEmpty(column, cells[row.id]?.[column.id]))
    );
  }
  return question.rows.every(
    (row) => requiredColumns.every(
      (column) => !isTableCellEmpty(column, cells[row.id]?.[column.id])
    )
  );
};
var isTableAnswerEmpty = (question, answer) => {
  const cells = getCells(answer);
  return !question.rows.some(
    (row) => question.columns.some((column) => !isTableCellEmpty(column, cells[row.id]?.[column.id]))
  );
};
var shouldShowTableRowHeader = (question) => question.rows.some((row, index) => {
  const label = row.label.trim().toLowerCase();
  const position = String(index + 1);
  return label !== position && label !== `row ${position}`;
});
var validateTableAnswer = (question, answer) => {
  const cells = getCells(answer);
  const rowIds = new Set(question.rows.map((row) => row.id));
  const columnsById = new Map(question.columns.map((column) => [column.id, column]));
  const issues = [];
  for (const row of question.rows) {
    for (const column of getRequiredTableColumns(question)) {
      if (isTableCellEmpty(column, cells[row.id]?.[column.id])) {
        issues.push({
          rowId: row.id,
          columnId: column.id,
          message: `${column.label} is required for ${row.label}`
        });
      }
    }
  }
  for (const [rowId, rowCells] of Object.entries(cells)) {
    if (!rowIds.has(rowId)) {
      continue;
    }
    for (const [columnId, value] of Object.entries(rowCells)) {
      const column = columnsById.get(columnId);
      if (!column) {
        continue;
      }
      const message = validateTableCell(column, value);
      if (message) {
        issues.push({ rowId, columnId, message });
      }
    }
  }
  return issues;
};
var mergeTableCellPatch = (existing, patch) => {
  const merged = { cells: { ...getCells(existing) } };
  const patchCells = getCells(patch);
  for (const [rowId, rowPatch] of Object.entries(patchCells)) {
    const currentRow = { ...merged.cells[rowId] ?? {} };
    for (const [columnId, value] of Object.entries(rowPatch)) {
      if (value === null) {
        delete currentRow[columnId];
      } else {
        currentRow[columnId] = value;
      }
    }
    if (Object.keys(currentRow).length > 0) {
      merged.cells[rowId] = currentRow;
    } else {
      delete merged.cells[rowId];
    }
  }
  return merged;
};

// src/features/forms/utils/elementTypes.ts
var mergeElementTypes = (existingTypes, usedTypes) => {
  const merged = [...existingTypes, ...usedTypes].filter(
    Boolean
  );
  const unique = Array.from(new Set(merged));
  return unique.length > 0 ? unique : [...DEFAULT_ELEMENT_TYPES];
};

// src/features/forms/utils/jump-logic-evaluator.ts
var NUMERIC_OPERATORS = /* @__PURE__ */ new Set(["gte", "lte", "between"]);
var STRING_OPERATORS = /* @__PURE__ */ new Set([
  "equals",
  "not_equals",
  "includes",
  "not_includes"
]);
var END_TARGET_ALIASES = /* @__PURE__ */ new Set([
  "end",
  "form_end",
  "end_of_form",
  "__end",
  "end__"
]);
var JumpLogicEvaluator = class {
  constructor(questions) {
    this.questions = questions;
    this.questionsByRef = new Map(questions.map((question) => [question.ref, question]));
    this.questionsByPageId = /* @__PURE__ */ new Map();
    questions.forEach((question) => {
      const pageId = question.pageId ?? "__unassigned";
      const existing = this.questionsByPageId.get(pageId) ?? [];
      existing.push(question);
      this.questionsByPageId.set(pageId, existing);
    });
  }
  questionsByRef;
  questionsByPageId;
  /**
   * @deprecated Question-level jump logic has been removed. Always returns null.
   */
  evaluateJump(_question, _currentAnswer, _allAnswers) {
    return null;
  }
  evaluateConditionForQuestion(condition, question, currentAnswer, allAnswers) {
    return this.evaluateCondition(condition, question, currentAnswer, allAnswers);
  }
  validatePageJumpRules(pages) {
    const errors = [];
    const orderedPages = [...pages].sort((a, b) => a.order - b.order);
    const pageIndexById = new Map(
      orderedPages.map((page, index) => [page.id, index])
    );
    const adjacency = /* @__PURE__ */ new Map();
    orderedPages.forEach((page, pageIndex) => {
      const pageQuestions = this.questionsByPageId.get(page.id) ?? [];
      const pageQuestionRefs = new Set(pageQuestions.map((question) => question.ref));
      for (const rule of page.jumpLogic ?? []) {
        for (const condition of rule.conditions ?? []) {
          if (condition.operator === "always") {
            continue;
          }
          if (!condition.questionRef) {
            errors.push(
              `Page "${page.id}" jump condition is missing questionRef`
            );
            continue;
          }
          if (!pageQuestionRefs.has(condition.questionRef)) {
            errors.push(
              `Page "${page.id}" jump condition references question "${condition.questionRef}" that is not on this page`
            );
          }
        }
        if (rule.target === JUMP_TARGET_END) {
          continue;
        }
        const targetIndex = pageIndexById.get(rule.target);
        if (targetIndex === void 0) {
          errors.push(
            `Page "${page.id}" jump rule "${rule.id}" targets unknown page "${rule.target}"`
          );
          continue;
        }
        if (targetIndex <= pageIndex) {
          errors.push(
            `Page "${page.id}" jump rule "${rule.id}" targets earlier page "${rule.target}"`
          );
        }
        const edges = adjacency.get(page.id) ?? /* @__PURE__ */ new Set();
        edges.add(rule.target);
        adjacency.set(page.id, edges);
      }
    });
    const visited = /* @__PURE__ */ new Set();
    const stack = /* @__PURE__ */ new Set();
    const detectCycle = (node) => {
      if (stack.has(node)) {
        return true;
      }
      if (visited.has(node)) {
        return false;
      }
      visited.add(node);
      stack.add(node);
      const neighbors = adjacency.get(node);
      if (neighbors) {
        for (const neighbor of neighbors) {
          if (detectCycle(neighbor)) {
            return true;
          }
        }
      }
      stack.delete(node);
      return false;
    };
    for (const page of orderedPages) {
      if (detectCycle(page.id)) {
        errors.push(`Detected circular page jump path starting at "${page.id}"`);
      }
    }
    return { valid: errors.length === 0, errors };
  }
  validateJumpCompleteness(pages) {
    const errors = [];
    pages.forEach((page) => {
      const rules = page.jumpLogic ?? [];
      if (rules.length === 0) {
        return;
      }
      const hasFallbackRule = rules.some(
        (rule) => (rule.conditions ?? []).some((condition) => condition.operator === "always")
      );
      const hasConditionalRules = rules.some(
        (rule) => (rule.conditions?.length ?? 0) > 0 && !(rule.conditions ?? []).some((condition) => condition.operator === "always")
      );
      if (hasConditionalRules && !hasFallbackRule) {
        errors.push(
          `Page "${page.id}" has conditional jump logic but no fallback rule. Add a lower-priority rule for the else case.`
        );
      }
      rules.forEach((rule) => {
        const normalizedTarget = rule.target.toLowerCase();
        if (END_TARGET_ALIASES.has(normalizedTarget) && rule.target !== JUMP_TARGET_END) {
          errors.push(
            `Jump target "${rule.target}" should be "${JUMP_TARGET_END}" (exact spelling required)`
          );
        }
      });
    });
    return { valid: errors.length === 0, errors };
  }
  validateJumpConditionValues(pages) {
    const errors = [];
    const extractStringValues = (value) => {
      if (typeof value === "string") {
        return [value];
      }
      if (Array.isArray(value)) {
        return value.filter((item) => typeof item === "string");
      }
      return [];
    };
    pages.forEach((page) => {
      const pageQuestions = this.questionsByPageId.get(page.id) ?? [];
      const pageQuestionRefs = new Set(pageQuestions.map((question) => question.ref));
      for (const rule of page.jumpLogic ?? []) {
        for (const condition of rule.conditions ?? []) {
          if (condition.operator === "always") {
            continue;
          }
          if (!condition.questionRef) {
            errors.push(
              `Page "${page.id}" jump condition is missing questionRef`
            );
            continue;
          }
          if (!pageQuestionRefs.has(condition.questionRef)) {
            errors.push(
              `Page "${page.id}" jump condition references question "${condition.questionRef}" that is not on this page`
            );
            continue;
          }
          const question = this.questionsByRef.get(condition.questionRef);
          if (!question) {
            continue;
          }
          if (this.isChoiceQuestion(question)) {
            if (NUMERIC_OPERATORS.has(condition.operator)) {
              errors.push(
                `Operator "between/gte/lte" used with choice question "${condition.questionRef}" - only valid for numeric questions`
              );
              continue;
            }
            if (!STRING_OPERATORS.has(condition.operator)) {
              continue;
            }
            const optionIds = /* @__PURE__ */ new Set([
              ...question.options.map((option) => option.id),
              JUMP_CONDITION_VALUE_OTHER
            ]);
            const valuesToCheck = extractStringValues(condition.value);
            valuesToCheck.forEach((value) => {
              if (!optionIds.has(value)) {
                errors.push(
                  `Jump condition value "${value}" not found in options of "${condition.questionRef}"`
                );
              }
            });
          }
        }
      }
    });
    return { valid: errors.length === 0, errors };
  }
  evaluateCondition(condition, question, currentAnswer, _allAnswers) {
    switch (condition.operator) {
      case "always":
        return true;
      case "is_empty":
        return this.isAnswerEmpty(question, currentAnswer);
      case "is_not_empty":
        return !this.isAnswerEmpty(question, currentAnswer);
      case "equals":
        return this.matchesEquals(condition.value, question, currentAnswer);
      case "not_equals":
        return !this.matchesEquals(condition.value, question, currentAnswer);
      case "includes":
        return this.matchesIncludes(condition.value, question, currentAnswer);
      case "not_includes":
        return !this.matchesIncludes(condition.value, question, currentAnswer);
      case "gte":
        return this.matchesComparison(
          condition.value,
          question,
          currentAnswer,
          (a, b) => a >= b
        );
      case "lte":
        return this.matchesComparison(
          condition.value,
          question,
          currentAnswer,
          (a, b) => a <= b
        );
      case "between":
        return this.matchesBetween(condition.value, question, currentAnswer);
      default:
        return false;
    }
  }
  isAnswerEmpty(question, answer) {
    if (answer === null || typeof answer === "undefined") {
      return true;
    }
    if (typeof answer === "string") {
      return answer.trim() === "";
    }
    if (Array.isArray(answer)) {
      return answer.length === 0;
    }
    if (question.type === "multiple_choice_single") {
      const single = this.asSingleChoice(answer);
      return !single?.optionId && !single?.otherText?.trim();
    }
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(answer);
      return (multi?.optionIds?.length ?? 0) === 0 && !multi?.otherText?.trim();
    }
    if (question.type === "table") {
      return isTableAnswerEmpty(question, answer);
    }
    if (typeof answer === "object") {
      return Object.keys(answer).length === 0;
    }
    return false;
  }
  matchesEquals(expected, question, currentAnswer) {
    if (expected === null || typeof expected === "undefined") {
      return currentAnswer === null || typeof currentAnswer === "undefined";
    }
    if (typeof expected === "number") {
      const value = this.asNumber(currentAnswer);
      return value !== null && value === expected;
    }
    if (typeof expected === "string") {
      if (expected === JUMP_CONDITION_VALUE_OTHER) {
        if (question.type === "multiple_choice_single") {
          const resolved = this.asSingleChoice(currentAnswer);
          return Boolean(resolved?.otherText?.trim() && !resolved?.optionId);
        }
        if (question.type === "multiple_choice_multi") {
          const resolved = this.asMultiChoice(currentAnswer);
          return Boolean(resolved?.otherText?.trim());
        }
      }
      const scalar = this.asScalarString(question, currentAnswer);
      if (scalar !== null) {
        return scalar === expected;
      }
      const selections = this.asArrayAnswer(question, currentAnswer);
      if (selections) {
        return selections.includes(expected);
      }
      return false;
    }
    if (Array.isArray(expected)) {
      const selections = this.asArrayAnswer(question, currentAnswer);
      if (selections) {
        return expected.every((value) => selections.includes(value));
      }
      const scalar = this.asScalarString(question, currentAnswer);
      if (scalar && expected.length === 1) {
        return expected[0] === scalar;
      }
      return false;
    }
    return false;
  }
  matchesIncludes(expected, question, currentAnswer) {
    if (typeof expected === "undefined") {
      return false;
    }
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(currentAnswer);
      if (!multi) {
        return false;
      }
      const targets = Array.isArray(expected) ? expected : [expected];
      const requireOther = targets.includes(JUMP_CONDITION_VALUE_OTHER);
      const resolvedTargets = targets.filter(
        (target) => typeof target === "string" && target !== JUMP_CONDITION_VALUE_OTHER
      );
      if (!resolvedTargets.every((target) => multi.optionIds.includes(target))) {
        return false;
      }
      if (!requireOther) {
        return true;
      }
      return Boolean(multi.otherText?.trim());
    }
    const selections = this.asArrayAnswer(question, currentAnswer);
    if (selections && selections.length > 0) {
      const targets = Array.isArray(expected) ? expected : [expected];
      const requireOther = targets.includes(JUMP_CONDITION_VALUE_OTHER);
      const resolvedTargets = targets.filter(
        (target) => typeof target === "string" && target !== JUMP_CONDITION_VALUE_OTHER
      );
      if (!resolvedTargets.every((target) => selections.includes(target))) {
        return false;
      }
      if (!requireOther) {
        return true;
      }
      return false;
    }
    const scalar = this.asScalarString(question, currentAnswer);
    if (typeof scalar === "string") {
      const targets = Array.isArray(expected) ? expected : [expected];
      if (targets.includes(JUMP_CONDITION_VALUE_OTHER)) {
        if (question.type === "multiple_choice_single") {
          const resolved = this.asSingleChoice(currentAnswer);
          return Boolean(resolved?.otherText?.trim() && !resolved?.optionId);
        }
        return false;
      }
      return targets.every(
        (target) => typeof target === "string" && scalar.includes(target)
      );
    }
    return false;
  }
  matchesBetween(expected, _question, currentAnswer) {
    if (!expected || typeof expected !== "object" || Array.isArray(expected)) {
      return false;
    }
    const candidate = expected;
    if (typeof candidate.min !== "number" || typeof candidate.max !== "number") {
      return false;
    }
    const value = this.asNumber(currentAnswer);
    if (value === null) {
      return false;
    }
    return value >= candidate.min && value <= candidate.max;
  }
  matchesComparison(expected, _question, currentAnswer, compare) {
    if (typeof expected !== "number") {
      return false;
    }
    const value = this.asNumber(currentAnswer);
    if (value === null) {
      return false;
    }
    return compare(value, expected);
  }
  asSingleChoice(value) {
    if (!value || typeof value !== "object") {
      return null;
    }
    const candidate = value;
    if (candidate.optionId || candidate.otherText) {
      return {
        optionId: candidate.optionId ?? void 0,
        otherText: candidate.otherText ?? void 0
      };
    }
    return null;
  }
  asMultiChoice(value) {
    if (!value || typeof value !== "object") {
      return null;
    }
    const candidate = value;
    const optionIds = Array.isArray(candidate.optionIds) ? candidate.optionIds.filter((id) => Boolean(id)) : [];
    const otherText = typeof candidate.otherText === "string" ? candidate.otherText.trim() : "";
    if (optionIds.length === 0 && !otherText) {
      return null;
    }
    return {
      optionIds,
      otherText: otherText || void 0
    };
  }
  asArrayAnswer(question, answer) {
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(answer);
      return multi ? multi.optionIds : null;
    }
    if (Array.isArray(answer)) {
      return answer.filter(
        (value) => typeof value === "string"
      );
    }
    return null;
  }
  asScalarString(question, answer) {
    if (typeof answer === "string") {
      const trimmed = answer.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (question.type === "multiple_choice_single") {
      const single = this.asSingleChoice(answer);
      if (!single) {
        return null;
      }
      if (single.optionId) {
        return single.optionId;
      }
      if (single.otherText) {
        const trimmed = single.otherText.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      return null;
    }
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(answer);
      if (!multi?.otherText) {
        return null;
      }
      return multi.otherText;
    }
    return null;
  }
  asNumber(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
  isChoiceQuestion(question) {
    return question.type === "multiple_choice_single" || question.type === "multiple_choice_multi";
  }
};

// src/features/forms/utils/legacy-logic-detector.ts
function detectLegacyJumpLogic(questions) {
  const issues = [];
  questions.forEach((question) => {
    const legacyQuestion = question;
    if (Array.isArray(legacyQuestion.jumpLogic) && legacyQuestion.jumpLogic.length > 0) {
      issues.push({
        type: "question_jump_logic",
        questionRef: question.ref,
        message: `Question "${question.ref}" includes legacy jumpLogic rules.`
      });
    }
    if (Array.isArray(legacyQuestion.options)) {
      legacyQuestion.options.forEach((option) => {
        if (!option.jumpTarget) {
          return;
        }
        const optionLabel = option.id ? ` option "${option.id}"` : " option";
        issues.push({
          type: "option_jump_target",
          questionRef: question.ref,
          message: `Question "${question.ref}"${optionLabel} includes a legacy jumpTarget.`
        });
      });
    }
  });
  return issues;
}

// src/features/forms/utils/visibility-evaluator.ts
var VisibilityEvaluator = class {
  constructor(questions) {
    this.questions = questions;
    this.questionsByRef = new Map(questions.map((q) => [q.ref, q]));
  }
  questionsByRef;
  /**
   * Determines if a question should be visible based on its visibleWhen condition
   * and current answers.
   *
   * @returns true if question should be shown, false if hidden
   */
  isVisible(question, answers) {
    if (!question.visibleWhen) {
      return true;
    }
    const { questionRef, conditions, match } = question.visibleWhen;
    const sourceQuestion = this.questionsByRef.get(questionRef);
    if (!sourceQuestion) {
      return true;
    }
    const sourceAnswer = answers[sourceQuestion.ref] ?? answers[sourceQuestion.id];
    const results = conditions.map(
      (condition) => this.evaluateCondition(condition, sourceQuestion, sourceAnswer)
    );
    return match === "all" ? results.every(Boolean) : results.some(Boolean);
  }
  /**
   * Filters a list of questions to only those that are visible.
   */
  filterVisible(questions, answers) {
    return questions.filter((q) => this.isVisible(q, answers));
  }
  /**
   * Validates visibility conditions for semantic correctness.
   * Ensures referenced questions exist, only look backwards,
   * and choice-based conditions target valid option ids.
   */
  validateVisibilityStructure() {
    const errors = [];
    const refToIndex = new Map(
      this.questions.map((question, index) => [question.ref, index])
    );
    this.questions.forEach((question, index) => {
      const visibleWhen = question.visibleWhen;
      if (!visibleWhen) {
        return;
      }
      const { questionRef, conditions } = visibleWhen;
      const referencedIndex = refToIndex.get(questionRef);
      if (referencedIndex === void 0) {
        errors.push(
          `Question "${question.ref}" visibility references unknown question "${questionRef}"`
        );
        return;
      }
      if (referencedIndex >= index) {
        errors.push(
          `Question "${question.ref}" (index ${index}) cannot reference "${questionRef}" (index ${referencedIndex}) - visibility can only reference earlier questions`
        );
      }
      const sourceQuestion = this.questionsByRef.get(questionRef);
      if (!sourceQuestion) {
        return;
      }
      if (this.isChoiceQuestion(sourceQuestion)) {
        const optionValues = new Set(sourceQuestion.options.map((option) => option.id));
        optionValues.add(JUMP_CONDITION_VALUE_OTHER);
        (conditions ?? []).forEach((condition) => {
          if ((condition.operator === "equals" || condition.operator === "not_equals") && typeof condition.value === "string" && !optionValues.has(condition.value)) {
            errors.push(
              `Visibility condition value "${condition.value}" not found in options of "${questionRef}"`
            );
          }
        });
      }
      const numericOperators = /* @__PURE__ */ new Set(["gte", "lte", "between"]);
      const hasNumericOperator = (conditions ?? []).some(
        (condition) => numericOperators.has(condition.operator)
      );
      if (hasNumericOperator && this.isChoiceQuestion(sourceQuestion)) {
        errors.push(
          `Operator "between/gte/lte" used with choice question "${questionRef}" - only valid for numeric questions`
        );
      }
    });
    return { valid: errors.length === 0, errors };
  }
  /**
   * Evaluates a single condition against a question's answer.
   * Mirrors the logic in JumpLogicEvaluator for consistency.
   */
  evaluateCondition(condition, question, answer) {
    switch (condition.operator) {
      case "always":
        return true;
      case "is_empty":
        return this.isAnswerEmpty(question, answer);
      case "is_not_empty":
        return !this.isAnswerEmpty(question, answer);
      case "equals":
        return this.matchesEquals(condition.value, question, answer);
      case "not_equals":
        return !this.matchesEquals(condition.value, question, answer);
      case "includes":
        return this.matchesIncludes(condition.value, question, answer);
      case "not_includes":
        return !this.matchesIncludes(condition.value, question, answer);
      case "gte":
        return this.matchesComparison(condition.value, answer, (a, b) => a >= b);
      case "lte":
        return this.matchesComparison(condition.value, answer, (a, b) => a <= b);
      case "between":
        return this.matchesBetween(condition.value, answer);
      default:
        return false;
    }
  }
  isAnswerEmpty(question, answer) {
    if (answer === null || typeof answer === "undefined") {
      return true;
    }
    if (typeof answer === "string") {
      return answer.trim() === "";
    }
    if (Array.isArray(answer)) {
      return answer.length === 0;
    }
    if (question.type === "multiple_choice_single") {
      const single = this.asSingleChoice(answer);
      return !single?.optionId && !single?.otherText?.trim();
    }
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(answer);
      return (multi?.optionIds?.length ?? 0) === 0 && !multi?.otherText?.trim();
    }
    if (question.type === "table") {
      return isTableAnswerEmpty(question, answer);
    }
    if (typeof answer === "object") {
      return Object.keys(answer).length === 0;
    }
    return false;
  }
  matchesEquals(expected, question, currentAnswer) {
    if (expected === null || typeof expected === "undefined") {
      return currentAnswer === null || typeof currentAnswer === "undefined";
    }
    if (typeof expected === "number") {
      const value = this.asNumber(currentAnswer);
      return value !== null && value === expected;
    }
    if (typeof expected === "string") {
      if (expected === JUMP_CONDITION_VALUE_OTHER) {
        if (question.type === "multiple_choice_single") {
          const resolved = this.asSingleChoice(currentAnswer);
          return Boolean(resolved?.otherText?.trim() && !resolved?.optionId);
        }
        if (question.type === "multiple_choice_multi") {
          const resolved = this.asMultiChoice(currentAnswer);
          return Boolean(resolved?.otherText?.trim());
        }
      }
      const scalar = this.asScalarString(question, currentAnswer);
      if (scalar !== null) {
        return scalar === expected;
      }
      const selections = this.asArrayAnswer(question, currentAnswer);
      if (selections) {
        return selections.includes(expected);
      }
      return false;
    }
    if (Array.isArray(expected)) {
      const selections = this.asArrayAnswer(question, currentAnswer);
      if (selections) {
        return expected.every((value) => selections.includes(value));
      }
      const scalar = this.asScalarString(question, currentAnswer);
      if (scalar && expected.length === 1) {
        return expected[0] === scalar;
      }
      return false;
    }
    return false;
  }
  matchesIncludes(expected, question, currentAnswer) {
    if (typeof expected === "undefined") {
      return false;
    }
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(currentAnswer);
      if (!multi) {
        return false;
      }
      const targets = Array.isArray(expected) ? expected : [expected];
      const requireOther = targets.includes("__other__");
      const resolvedTargets = targets.filter(
        (target) => typeof target === "string" && target !== "__other__"
      );
      if (!resolvedTargets.every((target) => multi.optionIds.includes(target))) {
        return false;
      }
      if (!requireOther) {
        return true;
      }
      return Boolean(multi.otherText?.trim());
    }
    const selections = this.asArrayAnswer(question, currentAnswer);
    if (selections && selections.length > 0) {
      const targets = Array.isArray(expected) ? expected : [expected];
      const resolvedTargets = targets.filter(
        (target) => typeof target === "string" && target !== "__other__"
      );
      return resolvedTargets.every((target) => selections.includes(target));
    }
    const scalar = this.asScalarString(question, currentAnswer);
    if (typeof scalar === "string") {
      const targets = Array.isArray(expected) ? expected : [expected];
      return targets.every(
        (target) => typeof target === "string" && scalar.includes(target)
      );
    }
    return false;
  }
  matchesBetween(expected, currentAnswer) {
    if (!expected || typeof expected !== "object" || Array.isArray(expected)) {
      return false;
    }
    const candidate = expected;
    if (typeof candidate.min !== "number" || typeof candidate.max !== "number") {
      return false;
    }
    const value = this.asNumber(currentAnswer);
    if (value === null) {
      return false;
    }
    return value >= candidate.min && value <= candidate.max;
  }
  matchesComparison(expected, currentAnswer, compare) {
    if (typeof expected !== "number") {
      return false;
    }
    const value = this.asNumber(currentAnswer);
    if (value === null) {
      return false;
    }
    return compare(value, expected);
  }
  asSingleChoice(value) {
    if (!value || typeof value !== "object") {
      return null;
    }
    const candidate = value;
    if (candidate.optionId || candidate.otherText) {
      return {
        optionId: candidate.optionId ?? void 0,
        otherText: candidate.otherText ?? void 0
      };
    }
    return null;
  }
  asMultiChoice(value) {
    if (!value || typeof value !== "object") {
      return null;
    }
    const candidate = value;
    const optionIds = Array.isArray(candidate.optionIds) ? candidate.optionIds.filter((id) => Boolean(id)) : [];
    const otherText = typeof candidate.otherText === "string" ? candidate.otherText.trim() : "";
    if (optionIds.length === 0 && !otherText) {
      return null;
    }
    return {
      optionIds,
      otherText: otherText || void 0
    };
  }
  asArrayAnswer(question, answer) {
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(answer);
      return multi ? multi.optionIds : null;
    }
    if (Array.isArray(answer)) {
      return answer.filter((value) => typeof value === "string");
    }
    return null;
  }
  asScalarString(question, answer) {
    if (typeof answer === "string") {
      const trimmed = answer.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (question.type === "multiple_choice_single") {
      const single = this.asSingleChoice(answer);
      if (!single) {
        return null;
      }
      if (single.optionId) {
        return single.optionId;
      }
      if (single.otherText) {
        const trimmed = single.otherText.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      return null;
    }
    if (question.type === "multiple_choice_multi") {
      const multi = this.asMultiChoice(answer);
      if (!multi?.otherText) {
        return null;
      }
      return multi.otherText;
    }
    return null;
  }
  isChoiceQuestion(question) {
    return question.type === "multiple_choice_single" || question.type === "multiple_choice_multi";
  }
  asNumber(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
};

// src/features/forms/utils/enrich-answers.ts
function enrichSelectionAnswers(answers, questions) {
  const questionByRef = /* @__PURE__ */ new Map();
  for (const q of questions) {
    questionByRef.set(q.ref, q);
  }
  const enriched = {};
  for (const [key, value] of Object.entries(answers)) {
    const question = questionByRef.get(key);
    if (question?.type === "multiple_choice_single" && value != null && typeof value === "object" && "optionId" in value) {
      const answer = value;
      const optionMap = new Map(
        (question.options ?? []).map((o) => [o.id, o.label])
      );
      enriched[key] = {
        ...answer,
        optionLabel: optionMap.get(answer.optionId) ?? answer.optionId
      };
    } else if (question?.type === "multiple_choice_multi" && value != null && typeof value === "object" && "optionIds" in value) {
      const answer = value;
      const optionMap = new Map(
        (question.options ?? []).map((o) => [o.id, o.label])
      );
      enriched[key] = {
        ...answer,
        optionLabels: answer.optionIds.map(
          (id) => optionMap.get(id) ?? id
        )
      };
    } else {
      enriched[key] = value;
    }
  }
  return enriched;
}

// src/features/branding/schemas.ts
import { z as z4 } from "zod";
var hexColorRegex2 = /^#[0-9A-Fa-f]{6}$/;
var hexColorSchema = z4.string().regex(hexColorRegex2, "Must be a valid hex color (e.g., #2563eb)");
var copyToneSchema = z4.object({
  tone: z4.enum(["professional", "friendly", "casual", "formal", "playful"]),
  voice: z4.enum(["first-person", "second-person", "third-person"]),
  suggestedIntroduction: z4.string().max(2e3).nullable(),
  suggestedThankYou: z4.string().max(2e3).nullable(),
  keywords: z4.array(z4.string().min(1).max(64)).default([])
});
var extractedBrandColorsSchema = z4.object({
  primary: hexColorSchema.nullable(),
  secondary: hexColorSchema.nullable(),
  accent: hexColorSchema.nullable(),
  background: hexColorSchema.nullable()
});
var extractedBrandFontsSchema = z4.object({
  primary: z4.string().max(100).nullable(),
  heading: z4.string().max(100).nullable()
});
var extractedBrandLogoSchema = z4.object({
  url: z4.string().url().max(500).nullable(),
  faviconUrl: z4.string().url().max(500).nullable(),
  appleTouchIcon: z4.string().url().max(500).nullable(),
  ogImage: z4.string().url().max(500).nullable()
});
var extractedBrandSchema = z4.object({
  sourceUrl: z4.string().url().max(500),
  colors: extractedBrandColorsSchema,
  fonts: extractedBrandFontsSchema,
  logo: extractedBrandLogoSchema,
  siteName: z4.string().max(200).nullable(),
  copyTone: copyToneSchema.nullable(),
  confidence: z4.number().min(0).max(1),
  extractedAt: isoDateTimeSchema
});
var brandPresetSchema = z4.object({
  id: objectIdSchema,
  projectId: objectIdSchema,
  name: z4.string().min(1).max(100),
  sourceUrl: z4.string().url().max(500).optional(),
  theme: formThemeSchema,
  copy: formCopySchema.optional(),
  createdBy: objectIdSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var extractBrandRequestSchema = z4.object({
  url: z4.string().url().max(500)
});
var createBrandPresetRequestSchema = z4.object({
  name: z4.string().min(1).max(100),
  sourceUrl: z4.string().url().max(500).optional(),
  theme: formThemeSchema,
  copy: formCopySchema.optional()
});

// src/features/webhooks/constants.ts
var WEBHOOK_EVENTS = [
  "submission.created",
  "submission.updated"
];
var WEBHOOK_DELIVERY_STATUSES = [
  "pending",
  "success",
  "failed"
];

// src/features/webhooks/schemas.ts
import { z as z5 } from "zod";
var webhookEventSchema = z5.enum(WEBHOOK_EVENTS);
var webhookDeliveryStatusSchema = z5.enum(WEBHOOK_DELIVERY_STATUSES);
var webhookSchema = z5.object({
  id: objectIdSchema,
  projectId: objectIdSchema,
  formId: objectIdSchema,
  url: z5.string().url(),
  secret: z5.string().min(1),
  events: z5.array(webhookEventSchema),
  enabled: z5.boolean(),
  createdBy: objectIdSchema,
  updatedBy: objectIdSchema.optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  lastTestedAt: isoDateTimeSchema.optional()
});
var webhookDeliverySchema = z5.object({
  id: objectIdSchema,
  webhookId: objectIdSchema,
  submissionId: objectIdSchema.optional(),
  status: webhookDeliveryStatusSchema,
  attemptCount: z5.number().int().min(0),
  lastAttemptedAt: isoDateTimeSchema.optional(),
  responseCode: z5.number().int().optional(),
  responseBody: z5.string().optional(),
  nextRetryAt: isoDateTimeSchema.optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var webhookTestResultSchema = z5.object({
  success: z5.boolean(),
  statusCode: z5.number().int().optional(),
  error: z5.string().optional()
});
var createWebhookRequestSchema = z5.object({
  url: z5.string().url(),
  events: z5.array(webhookEventSchema).min(1),
  enabled: z5.boolean().optional().default(true)
});
var updateWebhookRequestSchema = z5.object({
  url: z5.string().url().optional(),
  events: z5.array(webhookEventSchema).min(1).optional(),
  enabled: z5.boolean().optional()
});
var webhookPayloadSchema = z5.object({
  event: webhookEventSchema,
  formId: z5.string(),
  projectId: z5.string(),
  submissionId: z5.string(),
  timestamp: z5.string().describe("ISO 8601"),
  data: z5.object({
    answers: z5.record(z5.unknown()),
    metadata: z5.record(z5.unknown()).optional(),
    submittedAt: z5.string().describe("ISO 8601"),
    versionNumber: z5.number()
  })
});

// src/features/api-keys/constants.ts
var API_KEY_PREFIX = "mk_";
var API_KEY_SCOPES = [
  "submissions:read"
];

// src/features/api-keys/schemas.ts
import { z as z6 } from "zod";
var apiKeyScopeSchema = z6.enum(API_KEY_SCOPES);
var apiKeySchema = z6.object({
  id: objectIdSchema,
  projectId: objectIdSchema,
  name: z6.string().min(1).max(100),
  keyPrefix: z6.string(),
  scopes: z6.array(apiKeyScopeSchema),
  createdBy: objectIdSchema,
  lastUsedAt: isoDateTimeSchema.nullable().optional(),
  expiresAt: isoDateTimeSchema.nullable().optional(),
  revokedAt: isoDateTimeSchema.nullable().optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var createApiKeyRequestSchema = z6.object({
  name: z6.string().min(1).max(100),
  expiresAt: z6.string().datetime({ offset: true }).optional()
});
var apiKeyWithRawKeySchema = apiKeySchema.extend({
  key: z6.string()
});
var externalSubmissionMetadataSchema = z6.object({
  userAgent: z6.string().optional(),
  referrer: z6.string().optional()
});
var externalFileUploadSchema = z6.object({
  questionRef: z6.string(),
  fileName: z6.string(),
  fileSize: z6.number(),
  mimeType: z6.string(),
  url: z6.string().describe("Signed download URL")
});
var externalSubmissionSchema = z6.object({
  id: objectIdSchema,
  formId: objectIdSchema,
  projectId: objectIdSchema,
  versionNumber: z6.number().int().min(1),
  status: z6.enum(["in_progress", "completed", "abandoned"]),
  answers: z6.record(z6.unknown()),
  questionMetadata: z6.record(z6.unknown()).optional(),
  startedAt: isoDateTimeSchema,
  submittedAt: isoDateTimeSchema,
  completedAt: isoDateTimeSchema.nullable().optional(),
  durationMs: z6.number().nullable().optional(),
  metadata: externalSubmissionMetadataSchema.optional(),
  fileUploads: z6.array(externalFileUploadSchema).optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
var externalListSubmissionsResponseSchema = z6.object({
  submissions: z6.array(
    z6.object({
      id: objectIdSchema,
      projectId: objectIdSchema,
      formId: objectIdSchema,
      versionNumber: z6.number().int().min(1),
      status: z6.enum(["in_progress", "completed", "abandoned"]),
      startedAt: isoDateTimeSchema,
      submittedAt: isoDateTimeSchema,
      completedAt: isoDateTimeSchema.optional(),
      durationMs: z6.number().int().min(0).optional(),
      metadata: externalSubmissionMetadataSchema.optional()
    })
  ),
  total: z6.number()
});
var externalGetSubmissionResponseSchema = z6.object({
  submission: externalSubmissionSchema
});
var externalPaginationMetaSchema = z6.object({
  page: z6.number(),
  pageSize: z6.number(),
  totalPages: z6.number()
});
var externalErrorResponseSchema = z6.object({
  success: z6.literal(false),
  error: z6.object({
    message: z6.string(),
    code: z6.string(),
    details: z6.unknown().optional()
  })
});

// src/features/analytics/constants.ts
var FormEvents = {
  FORM_VIEWED: "form_viewed",
  FORM_SUBMITTED: "form_submitted",
  FORM_ABANDONED: "form_abandoned"
};

// src/features/analytics/posthog-client.ts
var PostHogClient = class {
  client = null;
  config = null;
  superProperties = {};
  initialized = false;
  isBrowser() {
    return typeof window !== "undefined";
  }
  init(config) {
    if (!this.isBrowser()) {
      if (config.debug) {
        console.warn("[Analytics] PostHog requires browser environment");
      }
      return;
    }
    if (config.disabled) {
      if (config.debug) {
        console.info("[Analytics] Tracking disabled");
      }
      return;
    }
    if (this.initialized) {
      return;
    }
    this.config = config;
    import("posthog-js").then(({ default: posthog }) => {
      posthog.init(config.apiKey, {
        api_host: config.apiHost ?? "https://us.i.posthog.com",
        loaded: (ph) => {
          this.client = ph;
          this.initialized = true;
          if (Object.keys(this.superProperties).length > 0) {
            ph.register(this.superProperties);
          }
          if (config.debug) {
            console.info("[Analytics] PostHog initialized");
          }
        },
        capture_pageview: false,
        capture_pageleave: true,
        disable_session_recording: !(config.sessionRecording ?? false),
        persistence: "localStorage"
      });
    });
  }
  track(eventName, properties) {
    if (!this.client) {
      if (this.config?.debug) {
        console.warn("[Analytics] Track called before initialization:", eventName);
      }
      return;
    }
    this.client.capture(eventName, {
      ...this.superProperties,
      ...properties,
      timestamp: properties?.timestamp ?? Date.now()
    });
  }
  identify(user) {
    if (!this.client) {
      if (this.config?.debug) {
        console.warn("[Analytics] Identify called before initialization");
      }
      return;
    }
    this.client.identify(user.id, {
      email: user.email,
      name: user.name,
      ...user.properties
    });
  }
  reset() {
    this.client?.reset();
  }
  pageView(options) {
    if (!this.client) return;
    this.client.capture("$pageview", {
      $current_url: options?.path ?? window.location.href,
      $title: options?.title ?? document.title,
      $referrer: options?.referrer ?? document.referrer,
      ...options?.properties
    });
  }
  setSuperProperties(properties) {
    this.superProperties = { ...this.superProperties, ...properties };
    this.client?.register(properties);
  }
  optOut() {
    this.client?.opt_out_capturing();
  }
  optIn() {
    this.client?.opt_in_capturing();
  }
  isOptedOut() {
    return this.client?.has_opted_out_capturing() ?? false;
  }
  async flush() {
  }
  isReady() {
    return this.initialized && this.client !== null;
  }
};

// src/features/analytics/noop-client.ts
var NoopAnalyticsClient = class {
  debug = false;
  init(config) {
    this.debug = config.debug ?? false;
    if (this.debug) {
      console.info("[Analytics:Noop] Initialized");
    }
  }
  track(eventName, properties) {
    if (this.debug) {
      console.info("[Analytics:Noop] Track:", eventName, properties);
    }
  }
  identify(user) {
    if (this.debug) {
      console.info("[Analytics:Noop] Identify:", user.id);
    }
  }
  reset() {
    if (this.debug) {
      console.info("[Analytics:Noop] Reset");
    }
  }
  pageView(options) {
    if (this.debug) {
      console.info("[Analytics:Noop] PageView:", options?.path);
    }
  }
  setSuperProperties(properties) {
    if (this.debug) {
      console.info("[Analytics:Noop] SetSuperProperties:", properties);
    }
  }
  optOut() {
  }
  optIn() {
  }
  isOptedOut() {
    return true;
  }
  async flush() {
  }
  isReady() {
    return true;
  }
};

// src/features/uploads/schemas.ts
import { z as z7 } from "zod";
var uploadedFileMetadataSchema = z7.object({
  fileKey: z7.string().min(1),
  fileName: z7.string().min(1),
  fileSize: z7.number().int().min(0),
  mimeType: z7.string().min(1),
  url: z7.string().min(1),
  uploadedAt: z7.string().datetime({ offset: true }).optional()
});
var createUploadSessionRequestSchema = z7.object({
  projectId: z7.string().min(1),
  formId: z7.string().min(1),
  version: z7.coerce.number().int().positive()
});
var createUploadSessionResponseSchema = z7.object({
  sessionToken: z7.string().min(1)
});

// src/features/billing/billing-config.ts
var SUBSCRIPTION_PLANS = {
  free: {
    id: "free",
    label: "Free",
    description: "Get started with basic features",
    price: 0,
    interval: null,
    featured: false,
    limits: {
      publishedForms: 3,
      submissionsPerMonth: 1e3,
      editors: 1,
      credits: 25
    },
    entitlements: {
      canUseWebhooks: false,
      canRemoveBranding: false,
      canUseMojoAssist: true
    }
  },
  starter: {
    id: "starter",
    label: "Starter",
    description: "Perfect for small teams",
    price: 19,
    interval: "month",
    featured: true,
    limits: {
      publishedForms: null,
      submissionsPerMonth: 5e3,
      editors: 1,
      credits: 100
    },
    entitlements: {
      canUseWebhooks: true,
      canRemoveBranding: true,
      canUseMojoAssist: true
    }
  },
  pro: {
    id: "pro",
    label: "Pro",
    description: "For growing teams and businesses",
    price: 29,
    interval: "month",
    featured: true,
    limits: {
      publishedForms: null,
      // unlimited
      submissionsPerMonth: 1e4,
      editors: 3,
      credits: 200
    },
    entitlements: {
      canUseWebhooks: true,
      canRemoveBranding: true,
      canUseMojoAssist: true
    }
  },
  enterprise: {
    id: "enterprise",
    label: "Enterprise",
    description: "Custom limits and dedicated support",
    price: null,
    // contact sales
    interval: null,
    featured: false,
    limits: {
      publishedForms: null,
      submissionsPerMonth: null,
      editors: null,
      credits: null
    },
    entitlements: {
      canUseWebhooks: true,
      canRemoveBranding: true,
      canUseMojoAssist: true
    }
  }
};
var CREDIT_PACKS = {
  small: {
    id: "small",
    label: "Small Pack",
    credits: 50,
    price: 4.99,
    description: "50 AI credits",
    popular: false
  },
  medium: {
    id: "medium",
    label: "Medium Pack",
    credits: 150,
    price: 9.99,
    description: "150 AI credits",
    popular: true
  },
  large: {
    id: "large",
    label: "Large Pack",
    credits: 400,
    price: 24.99,
    description: "400 AI credits",
    popular: false
  }
};
var getPlanConfig = (planId) => {
  return SUBSCRIPTION_PLANS[planId];
};
var getPlanLimits = (planId) => {
  return SUBSCRIPTION_PLANS[planId].limits;
};
var getPlanEntitlements = (planId) => {
  return SUBSCRIPTION_PLANS[planId].entitlements;
};
var getCreditPackConfig = (packId) => {
  return CREDIT_PACKS[packId];
};
var getCreditPackAmount = (packId) => {
  return CREDIT_PACKS[packId].credits;
};
var getAllPlans = () => Object.values(SUBSCRIPTION_PLANS);
var getAllCreditPacks = () => Object.values(CREDIT_PACKS);
var getPaidPlans = () => Object.values(SUBSCRIPTION_PLANS).filter(
  (p) => p.price !== null && p.price > 0
);
var isValidPlanId = (id) => {
  return id in SUBSCRIPTION_PLANS;
};
var isValidCreditPackId = (id) => {
  return id in CREDIT_PACKS;
};
var FREE_TRIAL_CREDITS = SUBSCRIPTION_PLANS.free.limits.credits;
var STARTER_MONTHLY_CREDITS = SUBSCRIPTION_PLANS.starter.limits.credits;
var PRO_MONTHLY_CREDITS = SUBSCRIPTION_PLANS.pro.limits.credits;

// src/features/billing/constants.ts
var BILLING_PLANS = ["free", "starter", "pro", "enterprise"];
var AI_CREDITS_EXHAUSTED_ERROR_CODE = "AI_CREDITS_EXHAUSTED";
var BILLING_LEDGER_ENTRY_TYPES = [
  "plan_changed",
  "credits_top_up",
  "manual_adjustment",
  "provider_event"
];
var BILLING_LEDGER_ENTRY_STATUSES = [
  "pending",
  "applied",
  "reversed"
];

// src/features/billing/schemas.ts
import { z as z8 } from "zod";
var billingPlanSchema = z8.enum(BILLING_PLANS);
var monthKeySchema = z8.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, "Invalid monthKey format (expected YYYY-MM)");
var billingLimitSchema = z8.number().int().nonnegative().nullable();
var billingLimitsSchema = z8.object({
  publishedForms: billingLimitSchema,
  submissionsPerMonth: billingLimitSchema,
  editors: billingLimitSchema,
  credits: billingLimitSchema
});
var billingEntitlementsSchema = z8.object({
  canUseWebhooks: z8.boolean(),
  canRemoveBranding: z8.boolean(),
  canUseMojoAssist: z8.boolean()
});
var projectBillingUsageSchema = z8.object({
  submissionsUsedThisMonth: z8.number().int().nonnegative(),
  creditsUsedThisMonth: z8.number().int().nonnegative(),
  topUpCreditsBalance: z8.number().int().nonnegative()
});
var projectBillingRemainingSchema = z8.object({
  submissionsRemainingThisMonth: billingLimitSchema,
  creditsRemainingThisMonth: billingLimitSchema
});
var billingPaymentInfoSchema = z8.object({
  provider: z8.enum(["polar"]).nullable(),
  isSubscribed: z8.boolean(),
  cancelAtPeriodEnd: z8.boolean(),
  currentPeriodEnd: z8.string().nullable()
  // ISO date string
});
var projectBillingSummarySchema = z8.object({
  plan: billingPlanSchema,
  limits: billingLimitsSchema,
  usage: projectBillingUsageSchema,
  remaining: projectBillingRemainingSchema,
  entitlements: billingEntitlementsSchema,
  period: z8.object({
    monthKey: monthKeySchema
  }),
  payment: billingPaymentInfoSchema.optional()
});

// src/features/billing/dto.ts
import { z as z9 } from "zod";
var adminSetPlanRequestSchema = z9.object({
  plan: billingPlanSchema,
  grantTrialCredits: z9.number().int().positive().optional(),
  grantTopUpCredits: z9.number().int().positive().optional()
});
var billingLedgerEntryTypeSchema = z9.enum(BILLING_LEDGER_ENTRY_TYPES);
var billingLedgerEntryStatusSchema = z9.enum(BILLING_LEDGER_ENTRY_STATUSES);
var projectBillingLedgerEntrySchema = z9.object({
  id: objectIdSchema,
  projectId: objectIdSchema,
  type: billingLedgerEntryTypeSchema,
  idempotencyKey: z9.string().min(1),
  status: billingLedgerEntryStatusSchema.optional(),
  initiatedByUserId: objectIdSchema.optional(),
  provider: z9.record(z9.unknown()).optional(),
  delta: z9.record(z9.unknown()).optional(),
  period: z9.object({
    monthKey: monthKeySchema
  }).optional(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema
});
export {
  AI_CREDITS_EXHAUSTED_ERROR_CODE,
  ALLOWED_LINK_PROTOCOLS,
  API_KEY_PREFIX,
  API_KEY_SCOPES,
  ASSISTANT_COMPONENT_TYPES,
  ASSISTANT_FOLLOW_UP_KINDS,
  ASSISTANT_PREFILL_STRATEGIES,
  BILLING_LEDGER_ENTRY_STATUSES,
  BILLING_LEDGER_ENTRY_TYPES,
  BILLING_PLANS,
  CONTENT_ELEMENT_TYPES,
  CREDIT_PACKS,
  DEFAULT_ELEMENT_TYPES,
  FORM_ELEMENT_TYPES,
  FORM_RESPONSE_MODES,
  FORM_VERSION_STATUSES,
  FREE_TRIAL_CREDITS,
  FormEvents,
  JUMP_CONDITION_OPERATORS,
  JUMP_CONDITION_VALUE_OTHER,
  JUMP_TARGET_END,
  JumpLogicEvaluator,
  LEGACY_DISPLAY_ELEMENT_TYPES,
  NON_ANSWERABLE_ELEMENT_TYPES,
  NoopAnalyticsClient,
  PROJECT_MEMBER_ROLES,
  PRO_MONTHLY_CREDITS,
  PostHogClient,
  STARTER_MONTHLY_CREDITS,
  SUBSCRIPTION_PLANS,
  TABLE_COLUMN_TYPES,
  TONE_PRESETS,
  VisibilityEvaluator,
  WEBHOOK_DELIVERY_STATUSES,
  WEBHOOK_EVENTS,
  adminSetPlanRequestSchema,
  analyticsTrendDateSchema,
  apiErrorSchema,
  apiKeySchema,
  apiKeyScopeSchema,
  apiKeyWithRawKeySchema,
  archiveFormRequestSchema,
  assistantConfigurationCopySchema,
  assistantConfigurationLayoutSchema,
  assistantConfigurationPageSchema,
  assistantConfigurationPagesSchema,
  assistantConfigurationQuestionPageIdsSchema,
  assistantSuggestedQuestionSchema,
  authOnboardingSchema,
  authSessionSchema,
  authSuccessSchema,
  authUserSchema,
  billingEntitlementsSchema,
  billingLedgerEntryStatusSchema,
  billingLedgerEntryTypeSchema,
  billingLimitSchema,
  billingLimitsSchema,
  billingPaymentInfoSchema,
  billingPlanSchema,
  brandPresetSchema,
  completeResponseSessionRequestSchema,
  completeResponseSessionResponseSchema,
  conversationMetadataSchema,
  conversationSessionStateSchema,
  conversationSettingsSchema,
  copyToneSchema,
  createApiKeyRequestSchema,
  createBrandPresetRequestSchema,
  createFormRequestSchema,
  createFormVersionRequestSchema,
  createProjectRequestSchema,
  createResponseSessionRequestSchema,
  createResponseSessionResponseSchema,
  createUploadSessionRequestSchema,
  createUploadSessionResponseSchema,
  createWebhookRequestSchema,
  detectLegacyJumpLogic,
  duplicateFormRequestSchema,
  emailNotificationSettingsSchema,
  enrichSelectionAnswers,
  externalErrorResponseSchema,
  externalFileUploadSchema,
  externalGetSubmissionResponseSchema,
  externalListSubmissionsResponseSchema,
  externalPaginationMetaSchema,
  externalSubmissionMetadataSchema,
  externalSubmissionSchema,
  extractBrandRequestSchema,
  extractedBrandColorsSchema,
  extractedBrandFontsSchema,
  extractedBrandLogoSchema,
  extractedBrandSchema,
  forgotPasswordSchema,
  formAnalyticsEventSchema,
  formAnalyticsFunnelNodeSchema,
  formAnalyticsLandingSchema,
  formAnalyticsOverviewSchema,
  formAnalyticsSummarySchema,
  formAnalyticsTrendPointSchema,
  formConversationLogicPatchSchema,
  formConversationLogicPatchesSchema,
  formConversationMessageMetadataSchema,
  formConversationMessageSchema,
  formConversationRoleSchema,
  formCopySchema,
  formElementPaletteSchema,
  formElementTypeSchema,
  formHeadlessSettingsSchema,
  formLayoutSchema,
  formPageSchema,
  formQuestionSchema,
  formResponseModeSchema,
  formSchema,
  formSchemaMetaSchema,
  formSchemaResultSchema,
  formStatsSchema,
  formThemeSchema,
  formUiMetadataSchema,
  formVersionSchema,
  formVersionStatusSchema,
  getAllCreditPacks,
  getAllPlans,
  getCreditPackAmount,
  getCreditPackConfig,
  getFormAnalyticsOverviewQuerySchema,
  getFormAnalyticsOverviewResponseSchema,
  getPaidPlans,
  getPlanConfig,
  getPlanEntitlements,
  getPlanLimits,
  getRequiredTableColumns,
  getResponseSessionResponseSchema,
  getSafeLinkHref,
  headlessCaptchaSettingsSchema,
  headlessRateLimitsSchema,
  isAllowedLinkUrl,
  isTableAnswerEmpty,
  isTableAnswered,
  isTableCellEmpty,
  isValidCreditPackId,
  isValidPlanId,
  isoDateTimeSchema,
  jsonSchema202012Schema,
  jumpConditionSchema,
  jumpRuleSchema,
  listFormsQuerySchema,
  listSubmissionsQuerySchema,
  loginSchema,
  markResponseSessionLeaveRequestSchema,
  mergeElementTypes,
  mergeTableCellPatch,
  monthKeySchema,
  objectIdSchema,
  orchestratorConversationMetadataSchema,
  orchestratorMetadataSchema,
  orchestratorPhaseSchema,
  parseLinkUrl,
  passwordPolicySchema,
  postSubmissionActionsSchema,
  projectBillingLedgerEntrySchema,
  projectBillingRemainingSchema,
  projectBillingSummarySchema,
  projectBillingUsageSchema,
  projectMemberRoleSchema,
  projectMemberSchema,
  projectSchema,
  projectWithFormsSchema,
  publicFormVersionSchema,
  publishFormVersionRequestSchema,
  questionBaseSchema,
  questionDocumentationSchema,
  questionValidationSchema,
  registerSchema,
  resetPasswordSchema,
  respondentConversationMetadataSchema,
  respondentStreamEventSchema,
  responseCaptureModeSchema,
  responseConversationStateSchema,
  responseExportFormatSchema,
  responseSchema,
  sharedFormStateResponseSchema,
  shouldShowTableRowHeader,
  submissionExportFormatSchema,
  submissionMetadataSchema,
  submissionStatusSchema,
  submissionSummarySchema,
  tableColumnSchema,
  tableColumnTypeSchema,
  tableRowSchema,
  tonePresetSchema,
  trackFormEventRequestSchema,
  uiDraftsSchema,
  updateFormRequestSchema,
  updateFormVersionRequestSchema,
  updateProjectRequestSchema,
  updateResponseSessionProgressRequestSchema,
  updateSharedFormStateRequestSchema,
  updateWebhookRequestSchema,
  uploadedFileMetadataSchema,
  validateTableAnswer,
  validateTableCell,
  visibilityConditionSchema,
  webhookDeliverySchema,
  webhookDeliveryStatusSchema,
  webhookEventSchema,
  webhookPayloadSchema,
  webhookSchema,
  webhookSettingsSchema,
  webhookTestResultSchema
};
//# sourceMappingURL=index.js.map